import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { phoneDigits, formatPhone } from '../../utils/phoneUtils';
import { noMoneyNoteOf } from './utils/contractRows';
import { useAuth } from '../../contexts/AuthContext';

// `switchBackOf(row)`: the page's Switch Back rule (utils/switchBack via contracts.logic) - truthy when the contract's
// own car is ready to go back to the customer
const RowActions = ({ row, switchBackOf, onCopy }) => {
    const { currentUser } = useAuth();
    const senderName = currentUser?.displayName || currentUser?.name || (currentUser?.email ? currentUser.email.split('@')[0] : 'the Yelo team');
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);

    const toggle = () => {
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const MENU_HEIGHT = 300; // Increased margin to prevent cutoff
            const isUp = spaceBelow < MENU_HEIGHT;

            setCoords({
                // Use viewport coordinates directly since wrapper is fixed
                top: isUp ? rect.top - 5 : rect.bottom + 5,
                left: rect.left,
                isUp
            });
        }
        setOpen(!open);
    };

    const handleAction = (action) => {
        action.action();
        setOpen(false);
    };

    // Close on scroll to avoid detached dropdowns
    useEffect(() => {
        if (open) {
            const onScroll = () => setOpen(false);
            window.addEventListener('scroll', onScroll, true);
            return () => window.removeEventListener('scroll', onScroll, true);
        }
    }, [open]);

    const copyAndOpenWhatsApp = (row, messageTemplate, toastMsg) => {
        const normalizedPhone = phoneDigits(row.phoneNumber);   // with its country code: a Saudi customer is 966...
        const refNo = row.contractNo || row.bookingNumber || 'XXXXXX';
        const message = messageTemplate.replace('#XXXXXX', `#${refNo}`);
        const encodedMessage = encodeURIComponent(message);
        onCopy(message, toastMsg);
        window.open(`https://api.whatsapp.com/send?phone=${normalizedPhone}&text=${encodedMessage}`, "_blank");
    };

    const getShortName = (name) => {
        if (!name) return "";
        const parts = name.trim().split(/\s+/);
        return parts.slice(0, 2).join(' ');
    };

    const shortName = getShortName(row.customer);

    // Invygo customers pay Invygo, never Yelo, and a company is settled by the accountant (owner, 28 Sep / 1 Oct
    // 2026): no "Kindly settle the payment" message to them. Same rule as the Net Balance '—' (contractRows).
    const invygo = !!noMoneyNoteOf(row);

    const options = [
        {
            label: 'Open WhatsApp', action: () => {
                const normalizedPhone = phoneDigits(row.phoneNumber);
                window.open(`https://api.whatsapp.com/send?phone=${normalizedPhone}`, "_blank");
            }
        },
        ...(invygo ? [] : [{
            label: 'Payment Invoice', action: () => {
                const template = `Dear ${shortName},\n\nThis is ${senderName} from Yelo Rent A Car.\n\nPlease find the attached invoice for your ${row.ejarModel} (Contract #${row.contractNo || 'XXXXXX'}). Kindly settle the payment at your earliest convenience to ensure continued service.\n\nIf already paid, please share the receipt. Thank you!`;
                copyAndOpenWhatsApp(row, template, 'Payment message copied!');
            }
        }]),
        ...(switchBackOf && switchBackOf(row) ? [{
            label: 'Switch Back Request', action: () => {
                // the car that is ready is the customer's own (the Invygo Plate column), not the one they have now
                const template = `Dear ${shortName},\n\nThis is ${senderName} from Yelo Rent A Car.\n\nYour vehicle (${[row.invygoModel, row.invygoPlate].filter(Boolean).join(' - ')}) is now ready as per your contract (#${row.contractNo || 'XXXXXX'}). We would appreciate it if you could kindly share your location so we can coordinate the swap for you today.\n\nThank you!`;
                copyAndOpenWhatsApp(row, template, 'Switch back request copied!');
            }
        }] : [])
    ].map((opt, i) => ({ ...opt, label: `${i + 1} - ${opt.label}` }));   // numbered as shown, no gap when one is left out

    const menu = open ? (
        <div className="fixed inset-0 z-[60] flex items-start justify-start" style={{ pointerEvents: 'none' }}>
            {/* Backdrop for click outside */}
            <div
                className="fixed inset-0 bg-transparent"
                onClick={() => setOpen(false)}
                style={{ pointerEvents: 'auto' }}
            />
            <div
                role="menu"
                className="absolute flex flex-col p-1 min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-md"
                style={{
                    top: coords.top,
                    left: coords.left,
                    transform: coords.isUp ? 'translateY(-100%)' : 'none',
                    pointerEvents: 'auto',
                }}
            >
                {options.map((opt, i) => (
                    <button
                        key={i}
                        role="menuitem"
                        onClick={(e) => { e.stopPropagation(); handleAction(opt); }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                        type="button"
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    ) : null;

    return (
        <>
            <button
                ref={buttonRef}
                className="ds-cell-phone hover:underline"
                onClick={(e) => { e.stopPropagation(); toggle(); }}
                type="button"
                aria-haspopup="true"
                aria-expanded={open}>
                {formatPhone(phoneDigits(row.phoneNumber))}
            </button>
            {open && ReactDOM.createPortal(menu, document.body)}
        </>
    );
};

export default RowActions;
